import { StepTask } from '@telespot/domain';
import type { Analysis, Finding, HashAlgorithm, ROISpecification } from '@telespot/domain';

import { checkIfOutOfBounds, FindingVersionRef, MosaicRoi, Roi } from '../../entities';

/** What the projection is expected to hold, and what excuses it from holding it. */
export interface ProjectionExpectations {
  /**
   * findingId -> the version this client's own save wrote. An exemption, not a wait: the
   * local store already holds those rows, so nothing on screen is waiting for them.
   */
  ownSaves: Map<string, FindingVersionRef>;
  /** findingId -> latest version per the source of record. */
  sourceOfRecord: Map<string, FindingVersionRef>;
  /** Whether a backfill has covered this (sample, analyst). */
  complete: boolean;
}

/** How far the projection has got against what the view is waiting for. */
export interface ProjectionProgress {
  /** Findings the projection is already holding, of `total`. */
  processed: number;
  /** Findings the source of record names and no local save explains. */
  total: number;
}

/**
 * Turns ROI findings into the rows the mosaic store holds, and says whether the store is
 * still holding them. Both halves read `PROJECTABLE_TYPES`, which is why they sit
 * together: a type projected by one and not expected by the other reads as permanent lag.
 */
export class RoiFindingProjectionService {
  /**
   * The finding types mirrored into the mosaic store. `ROITRACKING` is the deliberate
   * near miss: the source-of-record stats count its labels, and nothing projects it.
   */
  public static readonly PROJECTABLE_TYPES: ReadonlySet<string> = new Set([StepTask.POSITION, StepTask.ROIDETECTION]);

  public static isProjectable(type: string): boolean {
    return this.PROJECTABLE_TYPES.has(type);
  }

  /** The rows a finding amounts to: one per label per in-bounds box, deduplicated. */
  public static project(finding: Finding, analysis: Analysis, hashAlgo: HashAlgorithm): MosaicRoi[] {
    const content = finding.data?.content as ROISpecification[] | undefined;

    if (!Array.isArray(content)) return [];

    const findingId = finding.uuid ?? finding.id;
    const findingVersionId = finding.id;
    const assetId = analysis.assetId;
    const sampleId = analysis.sampleId;
    const pipelineId = analysis.pipelineId;

    const rois: MosaicRoi[] = [];

    for (const spec of content) {
      const x0 = Number(spec.x0);
      const y0 = Number(spec.y0);
      const x1 = Number(spec.x1);
      const y1 = Number(spec.y1);

      if (checkIfOutOfBounds({ x0, y0, x1, y1 } as MosaicRoi)) continue;

      for (const [label, score] of Object.entries(spec.labels ?? {})) {
        const id = hashAlgo.hash(JSON.stringify({ x0, y0, x1, y1, findingId }));
        const cropFile = hashAlgo.hash(JSON.stringify({ assetId, x0, x1, y0, y1 })) + '.jpg';

        const roi: MosaicRoi = {
          id,
          label,
          score,
          x0,
          x1,
          y0,
          y1,
          creatorEntity: finding.creatorEntity,
          creatorId: finding.creatorId,
          assetId,
          sampleId,
          analysisId: finding.analysisId ?? analysis.id,
          findingId,
          findingVersionId,
          findingVersion: finding.version,
          stepId: finding.pipelineStepId,
          pipelineId,
          cropFile,
        };

        const isDuplicate = rois.some((existing) => existing.id === roi.id && existing.label === roi.label);
        if (isDuplicate) continue;

        rois.push(roi);
      }
    }

    return rois;
  }

  /**
   * True when what the analyst is looking at predates the source of record, so the rows on
   * screen cannot be trusted and only a reload can make them current.
   *
   * The same question as "is the progress short of its total", so the banner and the bar
   * inside it cannot disagree.
   */
  public static isViewStale(expectations: ProjectionExpectations, projected: Map<string, FindingVersionRef>): boolean {
    return !this.isProgressComplete(this.progress(expectations, projected));
  }

  /**
   * Whether the projection is holding everything the view waits for.
   *
   * The one place this comparison is written. Everything that asks - the staleness verdict,
   * the poll's per-tick outcome, the bar's own reading - goes through here, because a second
   * copy of it is a second answer, and they feed different halves of the same banner.
   */
  public static isProgressComplete(progress: ProjectionProgress): boolean {
    return progress.processed >= progress.total;
  }

  /**
   * How much of what the view waits for the projection is already holding.
   *
   * Own saves are not counted at either end: the rows they produced are already in the
   * local store, so they are not something the analyst is waiting to see.
   */
  public static progress(
    expectations: ProjectionExpectations,
    projected: Map<string, FindingVersionRef>
  ): ProjectionProgress {
    let processed = 0;
    let total = 0;

    for (const [findingId, expected] of expectations.sourceOfRecord) {
      if (this.explainedByOwnSave(expected, expectations.ownSaves.get(findingId))) continue;

      total++;
      if (!this.isFindingStaleForView(findingId, expectations, projected)) processed++;
    }

    return { processed, total };
  }

  /**
   * Whether one finding the source of record names is absent from the projection or
   * recorded there as a different version.
   *
   * Absence counts only once the partition is `complete` - before a backfill it cannot be
   * told apart from a finding nothing has looked at yet. A recorded-but-different version
   * needs no such gate: its existence rules out a backfill gap.
   */
  private static isFindingStaleForView(
    findingId: string,
    expectations: ProjectionExpectations,
    projected: Map<string, FindingVersionRef>
  ): boolean {
    const expected = expectations.sourceOfRecord.get(findingId);

    if (!expected) return false;
    if (this.explainedByOwnSave(expected, expectations.ownSaves.get(findingId))) return false;

    const recorded = projected.get(findingId);

    if (this.disagrees(expected, recorded)) return true;

    return expectations.complete && recorded === undefined;
  }

  /**
   * True when this row predates a save made from here, so the local copy is the newer one.
   *
   * What makes it safe to keep editing while the projection catches up: a page read while
   * it lags brings back pre-save rows, and merging those would undo the save on screen.
   * Version-based, so it stops suppressing the moment the projection catches up.
   */
  public static supersededByOwnSave(
    row: Pick<Roi, 'findingId' | 'findingVersion'>,
    ownSaves: Map<string, FindingVersionRef>
  ): boolean {
    const ownSave = ownSaves.get(row.findingId);

    return ownSave !== undefined && row.findingVersion < ownSave.version;
  }

  /**
   * Whether our own save accounts for the source of record naming this version.
   *
   * Bounded by the version we wrote: a source of record *ahead* of our save means someone
   * else wrote after us, and their rows are not in our store. Equal numbers are not enough
   * to claim it - a reverted finding saved again reuses one, so `versionId` decides, exactly
   * as it does in `disagrees`. What remains ambiguous is a source of record *below* our
   * save: the snapshot predating our save and a rollback past it look identical from here,
   * and this reads it as the former.
   */
  private static explainedByOwnSave(expected: FindingVersionRef, ownSave?: FindingVersionRef): boolean {
    if (!ownSave) return false;

    return expected.versionId === ownSave.versionId || expected.version < ownSave.version;
  }

  /**
   * True when the projection records a different version row than expected. Keys on the row
   * id, not the number - numbers are assigned as `latest + 1`, so a reverted finding saved
   * again reuses one.
   */
  private static disagrees(expected: FindingVersionRef, recorded?: FindingVersionRef): boolean {
    return recorded !== undefined && recorded.versionId !== expected.versionId;
  }
}
